import { Suspense, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import { RouteFallback } from '@/components/common/RouteFallback';
import {
  LayoutDashboard,
  Search,
  Heart,
  BellRing,
  ClipboardList,
  Mail,
  FileText,
  Users,
  ShieldCheck,
  Share2,
  ListChecks,
  Settings as SettingsIcon,
  HelpCircle,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Avatar } from '@/components/ui/Avatar';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { RoleSwitcher } from '@/components/common/RoleSwitcher';
import { NotificationBell } from '@/components/common/NotificationBell';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  badge?: number;
}

const NAV: NavItem[] = [
  { to: '/portal/dashboard',           label: 'Dashboard',          Icon: LayoutDashboard },
  { to: '/portal/jobs',                label: 'Job search',         Icon: Search },
  { to: '/portal/saved',               label: 'Saved jobs',         Icon: Heart },
  { to: '/portal/job-alerts',          label: 'Job alerts',         Icon: BellRing },
  { to: '/portal/applications',        label: 'My applications',    Icon: ClipboardList },
  { to: '/portal/offers',              label: 'My offers',          Icon: Mail },
  { to: '/portal/resumes',             label: 'Resume manager',     Icon: FileText },
  { to: '/portal/referees',            label: 'Referees',           Icon: Users },
  { to: '/portal/compliance',          label: 'Compliance locker',  Icon: ShieldCheck },
  { to: '/portal/credential-pack',     label: 'Credential pack',    Icon: Share2 },
  { to: '/portal/checklist-documents', label: 'Checklist documents',Icon: ListChecks },
  { to: '/portal/profile',             label: 'Profile settings',   Icon: SettingsIcon },
];

export function PortalLayout() {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-bg">
      {/* Mobile overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-ink/40" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        data-no-print
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 transform bg-surface border-r border-border transition-transform lg:static lg:translate-x-0 print:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between px-5 border-b border-border">
          <Logo />
          <button
            className="lg:hidden rounded-lg p-1.5 text-ink-muted hover:bg-muted"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-col gap-0.5 p-3">
          {NAV.map(({ to, label, Icon, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/portal/dashboard'}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'bg-primary-soft text-primary'
                    : 'text-ink-muted hover:bg-muted hover:text-ink',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{label}</span>
              {badge != null && (
                <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-ink">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t border-border bg-surface p-3">
          <div className="flex items-center gap-3 rounded-lg p-2">
            <Avatar name={`${user?.firstName ?? ''} ${user?.surname ?? ''}`.trim() || 'Guest'} size="md" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">
                {user?.firstName} {user?.surname}
              </p>
              <p className="truncate text-xs text-ink-subtle">{user?.email}</p>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1">
            <Link
              to="/portal/help"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-ink-muted hover:bg-muted hover:text-ink"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              Help
            </Link>
            <button
              type="button"
              onClick={() => {
                signOut();
                navigate('/');
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-ink-muted hover:bg-muted hover:text-ink"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 min-w-0 flex-col">
        <header data-no-print className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/80 px-4 backdrop-blur lg:px-8 print:hidden">
          <button
            className="lg:hidden rounded-lg p-2 text-ink hover:bg-muted"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <RoleSwitcher />
          <NotificationBell to="/portal/notifications" />
          <ThemeToggle compact />
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-10 max-w-screen-2xl w-full mx-auto">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
